import {
    ActionMenu,
    Card,
    Colors,
    Heading,
    Icon,
    Pills,
    subtleHover,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    type ColorType,
    type OptionProps,
    type PillsStatus,
    type PillsVariant,
} from '@amelio/ui-library';

import type { FrequencyValue } from '@/constants/Frequency';
import type { CycleStatusTone } from '@/pages/CycleEvaluation/Dashboard/cycleStatus';

import {
    cycleCardRadius,
    cycleRowCardSpacing,
    cycleRowIconFrame,
    cycleRowIconSize,
} from '@/utils/cycleStyles';
import { getFrequencyLabel } from '@/utils/frequency';

const ROW_ICON = 'edit-clipboard-tick';

const TONE_PILL_STYLES: Readonly<
    Record<CycleStatusTone, { status?: PillsStatus; variant: PillsVariant; className?: string }>
> = {
    active: { status: 'active', variant: 'activeLight' },
    draft: { status: 'draft', variant: 'draftLight' },
    inactive: { status: 'inactive', variant: 'inactiveLight' },
    completed: { variant: 'default', className: 'border border-base-300' },
};

const COMPLETED_STATUS_DOT = 'h-2.5 w-2.5 shrink-0 rounded-full bg-base-400';

const TONE_ICON_COLORS: Readonly<Record<CycleStatusTone, ColorType>> = {
    active: Colors.STEP_RAIL_VIOLET,
    draft: Colors.BASE_400,
    inactive: Colors.BASE_400,
    completed: Colors.BASE_400,
};

/** A labelled secondary line under the title, e.g. the evaluation template behind a cycle. */
export interface CycleEvaluationRowDetail {
    label: string;
    value: string;
}

export interface CycleEvaluationRowProps {
    statusTone: CycleStatusTone;
    statusLabel: string;
    title: string;
    typeLabels: string[];
    actionMenuItems: OptionProps[];
    period?: string;
    schedule?: FrequencyValue;
    detail?: CycleEvaluationRowDetail;
    className?: string;
    dataTestId?: string;
    onRowClick?: () => void;
}

export const CycleEvaluationRow = ({
    statusTone,
    title = '',
    typeLabels = [],
    statusLabel = '',
    actionMenuItems,
    period,
    schedule,
    detail,
    className = '',
    dataTestId = 'cycle-evaluation-row',
    onRowClick,
}: CycleEvaluationRowProps) => {
    const wrapperClassName = `relative flex w-full items-center has-[button[aria-expanded="true"]]:z-50 ${className} ${onRowClick ? ` cursor-pointer ${subtleHover}` : ''}`;
    // Runtime values are not guaranteed to satisfy CycleStatusTone despite the type: this guard
    // is not dead code.
    const hasRecognizedStatusTone = Object.hasOwn(TONE_PILL_STYLES, statusTone);

    if (!hasRecognizedStatusTone) {
        console.error(
            `[performance-fe] CycleEvaluationRow received an unrecognized statusTone "${statusTone}"; rendering it as inactive instead of crashing the list.`,
        );
    }

    const {
        status: pillStatus,
        variant: pillVariant,
        className: pillClassName = '',
    } = hasRecognizedStatusTone ? TONE_PILL_STYLES[statusTone] : TONE_PILL_STYLES.inactive;
    const scheduleLabel = period ?? (schedule ? getFrequencyLabel(schedule) : undefined);

    return (
        <div
            className={wrapperClassName}
            data-testid={dataTestId}
            role={'article'}
            tabIndex={onRowClick ? 0 : undefined}
            aria-label={onRowClick ? title : undefined}
            onClick={onRowClick}
            onKeyDown={(event) => {
                if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    onRowClick();
                }
            }}
        >
            <Card
                variant={'flat'}
                padding={'none'}
                className={`flex w-full flex-wrap justify-between border max-md:p-[16px] md:flex-row md:flex-nowrap md:items-center md:p-[24px] ${cycleRowCardSpacing} ${cycleCardRadius}`}
                borderColor={'white'}
            >
                <div className={'flex flex-3 items-center'}>
                    <div
                        className={`bg-app-purple mr-[12px] flex shrink-0 items-center justify-center self-center rounded-lg ${cycleRowIconFrame}`}
                        aria-hidden={'true'}
                    >
                        <Icon
                            name={ROW_ICON}
                            className={cycleRowIconSize}
                            color={TONE_ICON_COLORS[statusTone]}
                        />
                    </div>

                    <div className={'flex min-w-0 flex-col'}>
                        <div className={'w-40 min-w-0 md:!w-60 xl:!w-100'}>
                            <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Heading
                                            level={5}
                                            className={'truncate overflow-hidden whitespace-nowrap'}
                                        >
                                            {title}
                                        </Heading>
                                    </TooltipTrigger>

                                    <TooltipContent
                                        side={'top'}
                                        align={'end'}
                                        className={'max-w-lg break-words whitespace-normal'}
                                    >
                                        {title}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>

                        {scheduleLabel && (
                            <Text
                                size={'small'}
                                color={Colors.BASE_400}
                            >
                                {scheduleLabel}
                            </Text>
                        )}

                        {detail && (
                            <div
                                className={'mt-1 flex min-w-0 items-baseline gap-1'}
                                data-testid={`${dataTestId}-detail`}
                            >
                                <Text
                                    size={'small'}
                                    color={Colors.BASE_400}
                                    className={'shrink-0'}
                                >
                                    {detail.label}
                                </Text>

                                <Text
                                    size={'small'}
                                    color={Colors.BASE_600}
                                    className={'truncate'}
                                >
                                    {detail.value}
                                </Text>
                            </div>
                        )}

                        {typeLabels.length > 0 && (
                            <div
                                className={'mt-3 flex flex-wrap gap-1'}
                                data-testid={`${dataTestId}-type-badges`}
                            >
                                {typeLabels.map((typeLabel) => (
                                    <Pills
                                        key={typeLabel}
                                        size={'sm'}
                                        variant={'purple'}
                                    >
                                        {typeLabel}
                                    </Pills>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div
                    className={
                        'flex items-center gap-[12px] max-md:order-1 max-md:w-full max-md:justify-between md:w-auto'
                    }
                >
                    <div className={'flex'}>
                        <Pills
                            size={'sm'}
                            status={pillStatus}
                            variant={pillVariant}
                            className={pillClassName}
                        >
                            {statusTone === 'completed' && (
                                <span
                                    className={COMPLETED_STATUS_DOT}
                                    aria-hidden={'true'}
                                />
                            )}

                            {statusLabel}
                        </Pills>
                    </div>

                    <div
                        className={'flex shrink-0'}
                        data-testid={`${dataTestId}-action-menu`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ActionMenu
                            items={actionMenuItems}
                            size={'sm'}
                            variant={'default'}
                            ariaLabel={'Evaluation actions menu'}
                            className={'border-base-300 bg-white'}
                        />
                    </div>
                </div>
            </Card>
        </div>
    );
};
